<template>
  <div class="affiliate-activity-card" :class="{ 'is-preview': preview }">
    <div class="affiliate-activity-card__head">
      <div class="affiliate-activity-card__title-row">
        <div class="affiliate-activity-card__title">Activity</div>
        <button
          v-if="preview"
          type="button"
          class="affiliate-activity-view-all"
          @click="$emit('view-all')"
        >
          View all
        </button>
      </div>
      <p v-if="preview" class="affiliate-activity-card__sub">
        Latest {{ displayedRows.length }}.
      </p>
    </div>

    <div v-if="!preview" class="affiliate-activity-filters">
      <input
        v-model="search"
        type="text"
        class="affiliate-activity-search"
        placeholder="Search user or order #..."
        @input="page = 1"
      />
      <select v-model="statusFilter" class="affiliate-activity-select" @change="page = 1">
        <option value="all">All statuses</option>
        <option value="pending">Pending</option>
        <option value="approved">Approved</option>
        <option value="requested">Requested</option>
      </select>
      <select v-model="dateRange" class="affiliate-activity-select" @change="page = 1">
        <option :value="30">Last 30 days</option>
        <option :value="90">Last 90 days</option>
        <option :value="365">Last 12 months</option>
        <option value="all">All time</option>
      </select>
    </div>

    <div class="table-responsive">
      <table class="affiliate-activity-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>{{ preview ? "Referred user" : "Referred user · order" }}</th>
            <th>Source</th>
            <th v-if="!preview">Via</th>
            <th>Order value</th>
            <th>You earn</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!displayedRows.length">
            <td :colspan="preview ? 6 : 7" class="affiliate-activity-empty">No activity yet.</td>
          </tr>
          <tr v-for="(row, index) in displayedRows" :key="rowKey(row, index)">
            <td>{{ formatDate(row.created_at) }}</td>
            <td>
              <div class="affiliate-activity-user">{{ referredName(row) }}</div>
              <div class="affiliate-activity-user-id" v-if="referredCode(row)">{{ referredCode(row) }}</div>
            </td>
            <td>
              <span class="affiliate-source-pill" :class="sourceClass(row)">{{ sourceLabel(row) }}</span>
            </td>
            <td v-if="!preview" class="affiliate-via-cell">{{ viaLabel(row) }}</td>
            <td>{{ numberFormat(row.order_total, "currency") }}</td>
            <td class="affiliate-earn-cell">{{ formatBonus(row.earned_bonus) }}</td>
            <td>
              <span class="affiliate-status-pill" :class="statusClass(row)">
                <span class="affiliate-status-dot"></span>
                {{ statusLabel(row) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <template v-if="!preview && filteredRows.length">
      <div class="affiliate-activity-footer">
        <div class="total-records-list">
          <p>Showing {{ displayedRows.length }} of {{ filteredRows.length }} records</p>
        </div>
        <div class="per-page-right-box">
          <div class="select-item">
            <select v-model.number="perPage" @change="changePagination()">
              <option :value="50">50</option>
              <option :value="100">100</option>
              <option :value="200">200</option>
              <option :value="500">500</option>
            </select>
          </div>
        </div>
      </div>
      <div class="pagination-list" v-if="lastPage > 1">
        <paginate
          :click-handler="goToPage"
          :prev-text="'Prev'"
          :next-text="'Next'"
          :page-count="lastPage"
          :page-range="3"
          :margin-pages="2"
          :container-class="'className'"
          :records="filteredRows.length"
          v-model="page"
          :per-page="perPage"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { numberFormat } from "@/prod.js";
import { truncateMoney } from "@/utils/money.js";
import Paginate from "vuejs-paginate-next";
import moment from "moment";

export default {
  components: {
    Paginate,
  },
  props: {
    earning_lists: {
      type: Array,
      default: () => [],
    },
    preview: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["view-all"],
  watch: {
    earning_lists: {
      immediate: true,
      handler(list) {
        this.earninglists = Array.isArray(list) ? list : [];
        this.page = 1;
      },
    },
  },
  data() {
    return {
      numberFormat,
      truncateMoney,
      earninglists: [],
      page: 1,
      perPage: 50,
      search: "",
      statusFilter: "all",
      dateRange: "all",
    };
  },
  methods: {
    formatBonus(value) {
      return this.numberFormat(this.truncateMoney(value), "currency");
    },
    goToPage(pageNum) {
      this.page = pageNum;
    },
    changePagination() {
      this.page = 1;
    },
    rowKey(row, index) {
      return row.id || row.transaction_id || `${row.agency_id}-${row.created_at}-${index}`;
    },
    parseDate(value) {
      if (!value) return null;
      const parsed = moment(value);
      return parsed.isValid() ? parsed : null;
    },
    formatDate(value) {
      const date = this.parseDate(value);
      return date ? date.format("MMM DD") : value || "—";
    },
    referredName(row) {
      return (
        row.referred_user ||
        row.agency_name ||
        row.user_name ||
        row.name ||
        row.affiliate ||
        row.agency_id ||
        "—"
      );
    },
    referredCode(row) {
      const code = row.order_id || row.lb_id || row.referral_id || row.user_id || row.id;
      if (code === undefined || code === null || code === "") return "";
      const text = String(code);
      return text.startsWith("#") ? text : `#${text}`;
    },
    sourceLabel(row) {
      const source = String(row.source || row.type || row.source_type || row.referral_type || "").toLowerCase();
      if (source.includes("code") || source.includes("coupon")) return "Referral Code";
      return "Link";
    },
    sourceClass(row) {
      return this.sourceLabel(row) === "Referral Code"
        ? "affiliate-source-pill--code"
        : "affiliate-source-pill--link";
    },
    viaLabel(row) {
      return (
        row.via ||
        row.slug ||
        row.affiliate_slug ||
        row.affiliate ||
        row.source_name ||
        "—"
      );
    },
    normalizedStatus(row) {
      return String(row.status || "").toLowerCase();
    },
    statusLabel(row) {
      const status = this.normalizedStatus(row);
      if (status === "paid") return "Approved";
      if (status === "requested") return "Requested";
      if (status === "pending" || status === "unpaid") return "Pending";
      return row.status || "—";
    },
    statusClass(row) {
      const status = this.normalizedStatus(row);
      if (status === "paid") return "is-approved";
      if (status === "requested") return "is-requested";
      return "is-pending";
    },
    matchesSearch(row) {
      const query = this.search.trim().toLowerCase();
      if (!query) return true;
      const haystack = [
        this.referredName(row),
        this.referredCode(row),
        this.viaLabel(row),
        row.agency_id,
        row.order_id,
        row.id,
      ]
        .filter((value) => value !== undefined && value !== null)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    },
    matchesStatus(row) {
      if (this.statusFilter === "all") return true;
      const status = this.normalizedStatus(row);
      if (this.statusFilter === "pending") return status === "pending" || status === "unpaid";
      if (this.statusFilter === "approved") return status === "paid";
      if (this.statusFilter === "requested") return status === "requested";
      return true;
    },
    matchesDate(row) {
      if (this.dateRange === "all") return true;
      const date = this.parseDate(row.created_at);
      if (!date) return true;
      return date.isSameOrAfter(moment().subtract(Number(this.dateRange), "days"), "day");
    },
  },
  computed: {
    sortedRows() {
      return [...this.earninglists].sort((a, b) => {
        const aDate = this.parseDate(a.created_at);
        const bDate = this.parseDate(b.created_at);
        if (aDate && bDate) return bDate.valueOf() - aDate.valueOf();
        return 0;
      });
    },
    filteredRows() {
      if (this.preview) return this.sortedRows;
      return this.sortedRows.filter(
        (row) =>
          this.matchesSearch(row) &&
          this.matchesStatus(row) &&
          this.matchesDate(row)
      );
    },
    lastPage() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.perPage));
    },
    displayedRows() {
      if (this.preview) return this.sortedRows.slice(0, 5);
      const start = (this.page - 1) * this.perPage;
      return this.filteredRows.slice(start, start + this.perPage);
    },
  },
};
</script>
